import React, { useMemo } from "react";
import classNames from "classnames";
import { CHECK, BILLABLE } from "icons";
import { Button } from "ui/index";
import { Checkbox } from "ui/components/checkbox";
import { Icon } from "components";
import HtmlContent from "@/components/html-content";
import { formatNotificationTime } from "utils/utils";
import { COLLECTION_ICONS } from "../constants";

export function useInboxColumns({
  status,
  allSelected,
  someSelected,
  toggleSelectAll,
  pageIds = [],
  selectedIds = [],
  toggleSelect,
  markReadMutation,
  bulkMutation,
}) {
  return useMemo(
    () => [
      ...(status === "inbox"
        ? [
            {
              id: "_select",
              size: 40,
              href: false,
              header: () => (
                <div
                  className="flex items-center justify-center pl-4 pr-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={
                      allSelected ? true : someSelected ? "indeterminate" : false
                    }
                    onCheckedChange={toggleSelectAll}
                    disabled={pageIds.length === 0}
                    aria-label="Select all notifications"
                  />
                </div>
              ),
              cell: ({ row }) => {
                const id = row.original?.id;
                const isSelected = selectedIds.includes(id);
                return (
                  <div
                    className="flex items-center justify-center pl-4 pr-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(checked) => toggleSelect(id, !!checked)}
                      aria-label={`Select notification ${id}`}
                    />
                  </div>
                );
              },
            },
          ]
        : []),
      {
        id: "collection",
        size: 40,
        accessorKey: "collection",
        cell: ({ row, getValue }) => {
          const IconComponent = COLLECTION_ICONS[getValue()] || BILLABLE;
          return (
            <span
              className={classNames(
                "w-5 h-5 flex items-center justify-center",
                row.original?.status !== "inbox" &&
                  "text-muted-foreground bg-zinc-100 dark:bg-zinc-800 rounded"
              )}
            >
              <Icon icon={IconComponent} className="w-5 h-5" />
            </span>
          );
        },
      },
      {
        id: "subject",
        accessorKey: "subject",
        cell: ({ row, getValue }) => (
          <span
            className={classNames(
              "text-small",
              row.original?.status === "inbox"
                ? "font-semibold text-foreground"
                : "text-muted-foreground"
            )}
          >
            {getValue()}
          </span>
        ),
      },
      {
        id: "message",
        accessorKey: "message",
        cell: ({ row, getValue }) => (
          <span
            className={classNames(
              "min-w-md whitespace-pre-wrap line-clamp-2",
              row.original?.status === "inbox"
                ? "font-medium text-foreground"
                : "text-muted-foreground"
            )}
          >
            <HtmlContent html={getValue()} />
          </span>
        ),
      },
      {
        id: "timestamp",
        accessorKey: "timestamp",
        cell: ({ row, getValue }) => {
          const value = getValue();
          const notificationId = row.original?.id;
          const notificationStatus = row.original?.status;

          return (
            <div
              className={classNames(
                "relative min-h-6 min-w-40 flex items-center justify-end pr-2",
                notificationStatus !== "inbox" && "text-muted-foreground"
              )}
            >
              <span
                className={classNames({
                  "text-small whitespace-nowrap transition-opacity duration-200 opacity-100 group-hover:hidden":
                    notificationStatus === "inbox",
                })}
              >
                {value ? formatNotificationTime(new Date(value)) : ""}
              </span>
              {notificationStatus === "inbox" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="hidden cursor-pointer group-hover:flex items-center gap-1.5 text-xs h-7 px-2.5 transition-all duration-150 border shadow-xs"
                  disabled={markReadMutation?.isLoading || bulkMutation?.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    markReadMutation?.mutateAsync({
                      id: notificationId,
                      status: "archived",
                    });
                  }}
                >
                  <Icon icon={CHECK} className="size-3.5" />
                  Clear
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [
      status,
      allSelected,
      someSelected,
      pageIds,
      selectedIds,
      markReadMutation?.isLoading,
      bulkMutation?.isPending,
      toggleSelectAll,
      toggleSelect,
    ]
  );
}
